import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { transformSchoolLevel } from '@backrs_ui/grow-ui/src/components/MarkersOfSuccess/markersOfSuccess.util';
import {
  CareerUserResponseSet,
  CareerUserResponseAnswer,
  SchoolLevel,
} from '@backrs_ui/grow-ui/src/models/models';
import { blueprintLubavApi } from '@backrs_ui/grow-ui/src/redux/blueprint/blueprint.lubav.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { selectDoesUserHaveAccessToken } from '@backrs_ui/grow-ui/src/redux/user/user';
import { postAPISurveyResults } from '@backrs_ui/grow-ui/src/redux/userResponse/thunks/userResponse.thunk';
import {
  addUserQuestionResponse,
  addUsersGraduationYear,
  addUsersSchool,
  addUsersSchoolLevel,
  addUsersStartTerm,
  addUsersStartYear,
  addUsersZipCode,
  clearDraftSubmitted,
  resetUserResponseSet,
  selectUserResponseSet,
  setAspirationsCategories,
} from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';
import { useDemoAwareRoute } from '@backrs_ui/grow-ui/src/utils/demoUtils';

import { Box, SxProps, Theme } from '@mui/material';
import { TailSpin } from 'react-loader-spinner';

import { enrollmentCodeToSchoolLevel, schoolLevelToEnrollmentCode } from './survey.util';
import { SurveyPage } from './SurveyPage';

export const SurveyPageContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const generatingBlueprintRoute = useDemoAwareRoute(APP_ROUTE.GENERATING_BLUEPRINT);

  const careerQuestions = useAppSelector((state) => state.survey.careerQuestions);

  const {
    careerPaths,
    careerPathRoles,
    careerPathMeanings,
    careerPathEnjoyments,
    careerCategories,
  } = useAppSelector((state) => state.careerPath);

  const userResponseSet: CareerUserResponseSet = useAppSelector(selectUserResponseSet);

  const hasAccessToken = useAppSelector(selectDoesUserHaveAccessToken);

  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !hasAccessToken,
  });
  const { data: profileOptions } = useFetchProfileValueOptionsQuery(undefined, {
    skip: !hasAccessToken,
  });
  const [updateUserProfile] = useUpdateUserProfileMutation();

  const profileEnrollmentCode = profileData?.profile.academicEnrollmentTypes?.code;

  const location = useLocation();
  const startAtLastQuestion = !!(location.state as { startAtLastQuestion?: boolean } | null)
    ?.startAtLastQuestion;

  // An already-submitted draft starts a blank quiz, except when "go back" from the
  // signup gate re-enters it; an in-progress draft is always kept.
  const draftSubmitted = useAppSelector((state) => state.userResponseSet.draftSubmitted);
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;

    didInitRef.current = true;

    if (draftSubmitted) {
      dispatch(startAtLastQuestion ? clearDraftSubmitted() : resetUserResponseSet());
    }
  }, [draftSubmitted, startAtLastQuestion, dispatch]);

  // Pre-fill the school level question from the user's profile enrollment
  useEffect(() => {
    if (!profileEnrollmentCode) return;

    const prefilled = enrollmentCodeToSchoolLevel(profileEnrollmentCode);

    if (prefilled) {
      dispatch(addUsersSchoolLevel(prefilled));
    }
  }, [profileEnrollmentCode, dispatch]);

  const handleSelectSchoolLevel = (schoolLevel: SchoolLevel) => {
    dispatch(addUsersSchoolLevel(schoolLevel));
  };

  const handleConfirmSchoolLevel = (schoolLevel: SchoolLevel) => {
    if (!hasAccessToken) return;

    // skip if user hasn't changed their answer
    if (schoolLevel === enrollmentCodeToSchoolLevel(profileEnrollmentCode)) return;

    const targetCode = schoolLevelToEnrollmentCode(schoolLevel);
    const matchingOption = profileOptions?.academicEnrollmentTypes?.find(
      (option) => option.code === targetCode,
    );

    if (matchingOption) {
      updateUserProfile({
        field: 'academicEnrollmentTypes',
        value: [String(matchingOption.id)],
      });
    }
  };

  const userHasBlueprint = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.user_responses,
  );

  const handleFinishSurvey = async (finalQuestionResponse?: CareerUserResponseAnswer) => {
    // Create an updated userResponseSet that includes the final question response
    const updatedUserResponseSet = finalQuestionResponse
      ? {
          ...userResponseSet,
          user_responses: [
            ...userResponseSet.user_responses.filter(
              (r) => r.career_question_id !== finalQuestionResponse.career_question_id,
            ),
            finalQuestionResponse,
          ],
        }
      : userResponseSet;

    // even for unauthenticated users, we call the thunk as it calculates the career path suggestions. We just wont post to the BE since there is no access token
    const resultAction = await dispatch(
      postAPISurveyResults({
        userResponseSet: updatedUserResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      }),
    );

    if (postAPISurveyResults.fulfilled.match(resultAction)) {
      navigate(generatingBlueprintRoute);

      if (hasAccessToken) {
        try {
          const responseSet = resultAction.payload.userResponseSet;

          const aspirationsCategoriesResponse = await dispatch(
            blueprintLubavApi.endpoints.fetchAspirationsCategories.initiate({
              response_set_id: responseSet.id,
            }),
          );

          if (aspirationsCategoriesResponse.data) {
            dispatch(setAspirationsCategories(aspirationsCategoriesResponse.data));
          } else if (aspirationsCategoriesResponse.error) {
            console.error('Aspirations categories failed:', aspirationsCategoriesResponse.error);
          }
        } catch (blueprintError) {
          console.error('Failed to prefetch aspirations:', blueprintError);
        }
      }

      return;
    }

    if (postAPISurveyResults.rejected.match(resultAction)) {
      console.error('Survey submission failed:', resultAction.payload);
    }
  };

  /** if the user selected 'trade' or 'no_current_enrollment', we use 'college' as the key. If the user selected 'high_school', we use 'high_school' */
  const workingSchoolLevel = transformSchoolLevel(userResponseSet.school_level);

  const onlyCareerQuestionsForSchoolLevel = careerQuestions.filter((question) =>
    ['both', workingSchoolLevel].includes(question.school_level),
  );

  if (!onlyCareerQuestionsForSchoolLevel.length) {
    return (
      <Box sx={loadingSpinnerStyle}>
        <TailSpin color="#000" height={50} width={50} />
      </Box>
    );
  }

  return (
    <SurveyPage
      careerQuestions={onlyCareerQuestionsForSchoolLevel}
      userResponseSet={userResponseSet}
      addUserQuestionResponse={(response) => dispatch(addUserQuestionResponse(response))}
      onFinishSurvey={handleFinishSurvey}
      onSelectSchoolLevel={handleSelectSchoolLevel}
      onConfirmSchoolLevel={handleConfirmSchoolLevel}
      userHasBlueprint={!!userHasBlueprint}
      careerPaths={careerPaths}
      careerCategories={careerCategories}
      isGuest={!hasAccessToken}
      onZipChange={(value) => dispatch(addUsersZipCode(value))}
      onSchoolChange={(value) => dispatch(addUsersSchool(value))}
      onGraduationYearChange={(value) => dispatch(addUsersGraduationYear(value))}
      onStartTermChange={(value) => dispatch(addUsersStartTerm(value))}
      onStartYearChange={(value) => dispatch(addUsersStartYear(value))}
    />
  );
};

const loadingSpinnerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
} as const;
